import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Select, Form, Table, Row, Col, Steps, 
  message, Divider, Typography, List, Tag, Modal, Empty,
  Spin, Descriptions, Collapse, InputNumber, Upload, Alert
} from 'antd';
import { 
  RightOutlined, CheckCircleOutlined, FileWordOutlined,
  FileTextOutlined, FileDoneOutlined, CheckOutlined, SelectOutlined, UploadOutlined
} from '@ant-design/icons';
import courseService from '../../services/courseService';
import { getSubject as getSubjectById, getSubjectsByCourse } from '../../services/subjectService';
import { generateExamService } from '../../services/generateExamService';
import type { 
  Course, 
  Subject, 
  ExamStructure, 
  DraftExam, 
  GeneratedExam,
  DraftExamsResponse 
} from '../../../../shared/types/database';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { Panel } = Collapse;

const GenerateExamPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [subjectInfo, setSubjectInfo] = useState<Subject | null>(null);
  const [draftExams, setDraftExams] = useState<DraftExam[]>([]);
  const [selectedDraftExamId, setSelectedDraftExamId] = useState<number | null>(null);
  const [selectedDraftExam, setSelectedDraftExam] = useState<DraftExam | null>(null);
  const [officialExam, setOfficialExam] = useState<DraftExam | null>(null);
  const [savedExamId, setSavedExamId] = useState<number | null>(null);
  const [customExamStructure, setCustomExamStructure] = useState<ExamStructure>({
    totalTime: 60,
    questions: [
      { time: 10, count: 2 },
      { time: 20, count: 1 }
    ]
  });
  const [form] = Form.useForm();
  const [answerFile, setAnswerFile] = useState<File | null>(null);
  const [uploadingAnswer, setUploadingAnswer] = useState(false);
  const [answerUploaded, setAnswerUploaded] = useState(false);

  // Lấy danh sách khóa học khi component được mount
  useEffect(() => {
    fetchCourses();
  }, []);

  // Lấy danh sách môn học khi khóa học thay đổi
  useEffect(() => {
    if (selectedCourse) {
      fetchSubjects(selectedCourse);
    } else {
      setSubjects([]);
      setSelectedSubject(null);
      setSubjectInfo(null);
    }
  }, [selectedCourse]);

  // Lấy thông tin môn học khi môn học thay đổi
  useEffect(() => {
    if (selectedSubject) {
      fetchSubjectInfo(selectedSubject);
    } else {
      setSubjectInfo(null);
    }
  }, [selectedSubject]);

  // Lấy danh sách khóa học từ API
  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await courseService.getCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách khóa học:', error);
      message.error('Không thể lấy danh sách khóa học');
    } finally {
      setLoading(false);
    }
  };

  // Lấy danh sách môn học từ API theo khóa học
  const fetchSubjects = async (khoaHocId: string) => {
    try {
      setLoading(true);
      const response = await getSubjectsByCourse(khoaHocId);
      setSubjects(response);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách môn học:', error);
      message.error('Không thể lấy danh sách môn học');
    } finally {
      setLoading(false);
    }
  };

  // Lấy thông tin chi tiết môn học
  const fetchSubjectInfo = async (monHocId: string) => {
    try {
      setLoading(true);
      const response = await getSubjectById(monHocId);
      setSubjectInfo(response);
    } catch (error) {
      console.error('Lỗi khi lấy thông tin môn học:', error);
      message.error('Không thể lấy thông tin môn học');
    } finally {
      setLoading(false);
    }
  };

  // Thêm hoặc loại bỏ một cấu trúc câu hỏi
  const addQuestionStructure = () => {
    const newStructure = { ...customExamStructure };
    newStructure.questions.push({ time: 5, count: 1 });
    setCustomExamStructure(newStructure);
  };

  const removeQuestionStructure = (index: number) => {
    const newStructure = { ...customExamStructure };
    newStructure.questions.splice(index, 1);
    setCustomExamStructure(newStructure);
  };

  // Cập nhật cấu trúc
  const updateQuestionStructure = (index: number, field: 'time' | 'count', value: number) => {
    const newStructure = { ...customExamStructure };
    newStructure.questions[index][field] = value;
    setCustomExamStructure(newStructure);
  };

  // Cập nhật tổng thời gian
  const updateTotalTime = (value: number) => {
    const newStructure = { ...customExamStructure };
    newStructure.totalTime = value;
    setCustomExamStructure(newStructure);
  };

  // Tạo 10 đề nháp
  const generateDraftExams = async (): Promise<void> => {
    try {
      // Kiểm tra cấu trúc đề thi
      const totalQuestionTime = customExamStructure.questions.reduce(
        (total, q) => total + (q.time * q.count), 0
      );
      
      if (totalQuestionTime > customExamStructure.totalTime) {
        message.error('Tổng thời gian của các câu hỏi không được vượt quá thời gian làm bài');
        return;
      }

      if (!selectedSubject || !selectedCourse) {
        message.error('Vui lòng chọn khóa học và môn học');
        return;
      }

      setLoading(true);
      const response = await generateExamService.generateDraftExams({
        monHocId: selectedSubject,
        khoaHocId: selectedCourse,
        examStructure: customExamStructure
      });
      
      setDraftExams(response.draftExams);
      setCurrentStep(1); // Chuyển sang bước chọn đề
      message.success('Tạo 10 đề nháp thành công');
    } catch (error: any) {
      console.error('Lỗi khi tạo đề nháp:', error);
      message.error('Không thể tạo đề nháp: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Chọn đề nháp
  const handleSelectDraftExam = (draftId: number) => {
    setSelectedDraftExamId(draftId);
    const selected = draftExams.find(draft => draft.id === draftId);
    setSelectedDraftExam(selected || null);
  };

  // Chọn ngẫu nhiên 1 đề chính thức
  const selectRandomOfficialExam = async () => {
    try {
      setLoading(true);
      
      const response = await generateExamService.selectOfficialExam({
        selectedDraftId: selectedDraftExamId || undefined,
        draftExams: draftExams
      });
      
      setOfficialExam(response.selectedExam);
      setCurrentStep(2); // Chuyển sang bước xem đề chính thức
      message.success('Đã chọn đề thi chính thức');
    } catch (error: any) {
      console.error('Lỗi khi chọn đề chính thức:', error);
      message.error('Không thể chọn đề chính thức: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Lưu đề thi chính thức
  const saveOfficialExam = async (): Promise<void> => {
    try {
      if (!officialExam) {
        message.error('Không có đề thi để lưu');
        return;
      }

      setLoading(true);
      const response = await generateExamService.saveOfficialExam({
        selectedExam: officialExam
      });
      
      setSavedExamId(response.deThiId);
      message.success('Lưu đề thi chính thức thành công');
      setCurrentStep(3); // Chuyển sang bước hoàn thành
    } catch (error: any) {
      console.error('Lỗi khi lưu đề thi chính thức:', error);
      message.error('Không thể lưu đề thi chính thức: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Tải file Word đề thi
  const downloadWordFile = async (): Promise<void> => {
    try {
      if (!savedExamId) {
        message.error('Không có đề thi để tải');
        return;
      }

      setLoading(true);
      await generateExamService.downloadWordFile(savedExamId);
      message.success('Tải file Word thành công');
    } catch (error: any) {
      console.error('Lỗi khi tải file Word:', error);
      message.error('Không thể tải file Word: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Reset quá trình tạo đề thi
  const resetProcess = () => {
    setCurrentStep(0);
    setDraftExams([]);
    setSelectedDraftExamId(null);
    setSelectedDraftExam(null);
    setOfficialExam(null);
    setSavedExamId(null);
    setAnswerFile(null);
    setAnswerUploaded(false);
    form.resetFields();
  };

  // Upload file đáp án
  const uploadAnswerFile = async () => {
    if (!answerFile || !savedExamId) {
      message.error('Vui lòng chọn file đáp án trước');
      return;
    }

    try {
      setUploadingAnswer(true);
      
      await generateExamService.uploadAnswerFile(savedExamId, answerFile);
      
      message.success('Upload đáp án thành công');
      setAnswerUploaded(true);
    } catch (error: any) {
      console.error('Lỗi khi upload đáp án:', error);
      message.error('Không thể upload đáp án: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingAnswer(false);
    }
  };

  // Tải xuống file đáp án
  const downloadAnswerFile = async (): Promise<void> => {
    try {
      if (!savedExamId) {
        message.error('Không có đề thi để tải đáp án');
        return;
      }

      setLoading(true);
      await generateExamService.downloadAnswerFile(savedExamId);
      message.success('Tải file đáp án thành công');
    } catch (error: any) {
      console.error('Lỗi khi tải file đáp án:', error);
      message.error('Không thể tải file đáp án: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Render tóm tắt đề thi
  const renderExamSummary = (exam: DraftExam) => {
    if (!exam) return null;
    
    return (
      <div>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Môn học">{exam.tenMonHoc}</Descriptions.Item>
          <Descriptions.Item label="Thời gian làm bài">{exam.tongThoiGianLamBai} phút</Descriptions.Item>
        </Descriptions>
        
        <Divider orientation="left">Câu hỏi</Divider>
        
        <List
          dataSource={exam.cauHoi}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={<span>Câu {item.thuTu}: <Tag color="blue">{item.thoiGianDuKien} phút</Tag></span>}
                description={item.noiDungCauHoi.length > 100 ? 
                  item.noiDungCauHoi.substring(0, 100) + '...' : 
                  item.noiDungCauHoi}
              />
            </List.Item>
          )}
        />
      </div>
    );
  };

  // Nội dung các bước
  const steps = [
    {
      title: 'Chọn môn học',
      content: (
        <Card>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Khóa học" required>
                  <Select
                    placeholder="Chọn khóa học"
                    value={selectedCourse}
                    onChange={(value) => setSelectedCourse(value)}
                    style={{ width: '100%' }}
                    loading={loading}
                  >
                    {courses.map(course => (
                      <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                        {course.TenKhoaHoc}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Môn học" required>
                  <Select
                    placeholder="Chọn môn học"
                    value={selectedSubject}
                    onChange={(value) => setSelectedSubject(value)}
                    style={{ width: '100%' }}
                    disabled={!selectedCourse}
                    loading={loading}
                  >
                    {subjects.map(subject => (
                      <Option key={subject.MonHocID} value={subject.MonHocID}>
                        {subject.TenMonHoc}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {subjectInfo && (
              <>
                <Divider />
                <Title level={5}>Thông tin môn học</Title>
                <Descriptions bordered size="small">
                  <Descriptions.Item label="Mã môn học" span={3}>{subjectInfo.MonHocID}</Descriptions.Item>
                  <Descriptions.Item label="Tên môn học" span={3}>{subjectInfo.TenMonHoc}</Descriptions.Item>
                  <Descriptions.Item label="Số tín chỉ" span={3}>{subjectInfo.SoTC}</Descriptions.Item>
                </Descriptions>

                <Divider />
                <Title level={5}>Cấu trúc đề thi</Title>
                
                <Form.Item label="Tổng thời gian làm bài (phút)">
                  <InputNumber 
                    min={15} 
                    step={5}
                    value={customExamStructure.totalTime}
                    onChange={(value) => updateTotalTime(value || 60)}
                    style={{ width: 200 }}
                  />
                </Form.Item>
                
                <Divider orientation="left">Cấu trúc câu hỏi</Divider>
                
                {customExamStructure.questions.map((q, index) => (
                  <Row key={index} gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                      <Form.Item label={`Câu hỏi ${index + 1}: Thời gian (phút)`}>
                        <InputNumber
                          min={5}
                          step={5}
                          value={q.time}
                          onChange={(value) => updateQuestionStructure(index, 'time', value || 5)}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Số lượng câu hỏi">
                        <InputNumber
                          min={1}
                          value={q.count}
                          onChange={(value) => updateQuestionStructure(index, 'count', value || 1)}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Button 
                        danger 
                        onClick={() => removeQuestionStructure(index)}
                        disabled={customExamStructure.questions.length <= 1}
                      >
                        Xóa
                      </Button>
                    </Col>
                  </Row>
                ))}
                
                <Button type="dashed" onClick={addQuestionStructure} style={{ marginBottom: 16 }}>
                  + Thêm loại câu hỏi
                </Button>
                
                <div style={{ marginTop: 16 }}>
                  <Text>
                    Tổng thời gian câu hỏi: {customExamStructure.questions.reduce((sum, q) => sum + (q.time * q.count), 0)} phút
                  </Text>
                </div>
              </>
            )}

            <Divider />
            <Button 
              type="primary" 
              onClick={generateDraftExams}
              disabled={!selectedSubject}
              loading={loading}
            >
              Tạo đề nháp <RightOutlined />
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      title: 'Chọn đề thi',
      content: (
        <Card>
          {draftExams.length === 0 ? (
            <Empty description="Chưa có đề nháp" />
          ) : (
            <>
              <Title level={5}>Chọn 1 trong 10 đề thi nháp đã tạo</Title>
              
              <div style={{ marginBottom: 16 }}>
                <Button 
                  type="primary" 
                  onClick={selectRandomOfficialExam}
                  loading={loading}
                >
                  <CheckCircleOutlined /> Chọn đề chính thức
                </Button>
                <Text style={{ marginLeft: 8 }}>
                  {selectedDraftExamId ? 'Sử dụng đề đã chọn' : 'Chọn ngẫu nhiên từ các đề nháp'}
                </Text>
              </div>
              
              <Row gutter={[16, 16]}>
                {draftExams.map(draft => (
                  <Col key={draft.id} span={8}>
                    <Card
                      title={`Đề thi ${draft.id}`}
                      extra={
                        selectedDraftExamId === draft.id ? (
                          <Tag color="green"><CheckOutlined /> Đã chọn</Tag>
                        ) : (
                          <Button 
                            size="small" 
                            onClick={() => handleSelectDraftExam(draft.id)}
                          >
                            <SelectOutlined /> Chọn
                          </Button>
                        )
                      }
                      style={{ 
                        border: selectedDraftExamId === draft.id ? '2px solid #52c41a' : '1px solid #d9d9d9'
                      }}
                    >
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Môn học">{draft.tenMonHoc}</Descriptions.Item>
                        <Descriptions.Item label="Thời gian">{draft.tongThoiGianLamBai} phút</Descriptions.Item>
                        <Descriptions.Item label="Số câu hỏi">{draft.cauHoi.length}</Descriptions.Item>
                      </Descriptions>
                      
                      <Collapse ghost>
                        <Panel header="Chi tiết câu hỏi" key="1">
                          <List
                            size="small"
                            dataSource={draft.cauHoi}
                            renderItem={item => (
                              <List.Item>
                                <Text>
                                  Câu {item.thuTu}: <Tag color="blue">{item.thoiGianDuKien} phút</Tag>
                                </Text>
                              </List.Item>
                            )}
                          />
                        </Panel>
                      </Collapse>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Card>
      ),
    },
    {
      title: 'Đề chính thức',
      content: (
        <Card>
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>Đề thi chính thức đã chọn</Title>
            <Paragraph>
              Đây là đề thi chính thức đã được chọn. Hãy xem chi tiết và xác nhận.
            </Paragraph>
          </div>
          
          {officialExam && (
            <div>
              <Card
                title={`Đề thi môn ${officialExam.tenMonHoc}`}
                bordered
                style={{ marginBottom: 24 }}
              >
                {renderExamSummary(officialExam)}
              </Card>
              
              <div style={{ marginTop: 24, textAlign: 'center' }}>
                <Button 
                  type="primary" 
                  onClick={saveOfficialExam}
                  loading={loading}
                  icon={<FileDoneOutlined />}
                >
                  Lưu và Hoàn tất
                </Button>
              </div>
            </div>
          )}
        </Card>
      ),
    },
    {
      title: 'Hoàn thành',
      content: (
        <Card>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
            <Title level={3}>Đề thi đã được lưu thành công!</Title>
            <Paragraph>
              Đề thi chính thức đã được lưu vào hệ thống. Bạn có thể tải xuống file Word hoặc xem lại đề thi trong "Lịch sử Đề thi".
            </Paragraph>
            
            <div style={{ marginTop: 24 }}>
              <Button 
                type="primary" 
                onClick={downloadWordFile}
                loading={loading}
                icon={<FileWordOutlined />}
                style={{ marginRight: 16 }}
              >
                Tải xuống file Word
              </Button>
              
              <Button 
                onClick={resetProcess}
                icon={<RightOutlined />}
              >
                Tạo đề thi mới
              </Button>
            </div>

            <Divider style={{ margin: '24px 0' }}>Đáp án đề thi</Divider>
            
            <div style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto' }}>
              <Paragraph>
                Bạn có thể tải lên file đáp án cho đề thi này. Đáp án sẽ được lưu và có thể tải xuống sau này.
              </Paragraph>
              
              {!answerUploaded ? (
                <div>
                  <Upload
                    maxCount={1}
                    beforeUpload={(file) => {
                      setAnswerFile(file);
                      return false; // Ngăn upload tự động
                    }}
                    onRemove={() => setAnswerFile(null)}
                    accept=".doc,.docx,.pdf,.xls,.xlsx,.txt"
                  >
                    <Button icon={<UploadOutlined />}>Chọn file đáp án</Button>
                  </Upload>
                  
                  <Button
                    type="primary"
                    onClick={uploadAnswerFile}
                    disabled={!answerFile}
                    loading={uploadingAnswer}
                    style={{ marginTop: 16 }}
                  >
                    Tải lên đáp án
                  </Button>
                </div>
              ) : (
                <div>
                  <Alert
                    message="Đã tải lên file đáp án thành công"
                    type="success"
                    showIcon
                    action={
                      <Button 
                        size="small" 
                        type="primary"
                        onClick={downloadAnswerFile}
                        loading={loading}
                      >
                        Tải xuống
                      </Button>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      ),
    },
  ];

  return (
    <div className="generate-exam-page">
      <Card title="Tạo Đề Thi Tự Luận" bordered={false}>
        <Steps current={currentStep} onChange={setCurrentStep}>
          {steps.map(item => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
        
        <div className="steps-content" style={{ marginTop: 24 }}>
          {steps[currentStep].content}
        </div>
        
        <div className="steps-action" style={{ marginTop: 24, textAlign: 'right' }}>
          {currentStep > 0 && (
            <Button style={{ margin: '0 8px' }} onClick={() => setCurrentStep(currentStep - 1)}>
              Quay lại
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default GenerateExamPage;
